import type { CSSProperties } from 'react';
import Dotdotdot from 'react-dotdotdot';

import { Box } from '@hivebrite/design-system/Box';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';

import type { ITopic } from 'hb-react/shared/apps/TopicsIndexV2/types';
import { partialLocation } from 'helpers/postalAddress';

const getDisplayCssProperty = (
  displayNoneIfDetailsEmpty: boolean,
  detail: string | JSX.Element,
  display?: CSSProperties['display'],
) => {
  const getDisplayOrInlineBlock = () => display || 'inline-block';
  const getDetailCondition = () =>
    detail ? getDisplayOrInlineBlock() : 'none';

  return displayNoneIfDetailsEmpty
    ? getDetailCondition()
    : getDisplayOrInlineBlock();
};

interface TopicDetailProps {
  display?: string;
  displayNoneIfDetailsEmpty?: boolean;
  mb?: number;
  topic: Pick<ITopic, 'total_followings' | 'location'>;
}

const TopicDetail = ({
  display,
  displayNoneIfDetailsEmpty = false,
  mb,
  topic,
}: TopicDetailProps) => {
  const { total_followings: totalFollowings, location } = topic;

  // Define following text depending on threshold
  // Admin can hide the number of members in the topic (totalFollowings is then null)
  const totalFollowingsText =
    totalFollowings === null
      ? null
      : n__('%{count} member', '%{count} members', totalFollowings).i__({
          count: String(totalFollowings),
        });

  const locationText: string = partialLocation(location);

  const detail =
    totalFollowingsText && locationText ? (
      <>
        {totalFollowingsText}
        <ScreenReadersContent>. {__('Location')}:</ScreenReadersContent>
        <span aria-hidden>{' · '}</span>
        {locationText}
      </>
    ) : (
      totalFollowingsText || locationText
    );

  return (
    <Box
      dataTestId="topic-detail"
      mx={4}
      mb={mb}
      display={getDisplayCssProperty(
        displayNoneIfDetailsEmpty,
        detail,
        display,
      )}
    >
      <Dotdotdot clamp={2} useNativeClamp>
        {detail}
      </Dotdotdot>
    </Box>
  );
};

export default TopicDetail;
